import { NgModule, ErrorHandler, LOCALE_ID } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import * as Sentry from '@sentry/angular';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { NgxMdModule } from 'ngx-md';
import {
  provideNgxWebstorage,
  withLocalStorage,
  withNgxWebstorageConfig,
  withSessionStorage
} from 'ngx-webstorage';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { AccountModule } from './account/account.module';
import { ContentComponent } from './content.component';
import { ContentTitleService } from './content-title.service';
import { SharedModule, HttpLoaderFactory } from './shared/shared.module';

import { registerLocaleData } from '@angular/common';
import localeSv from '@angular/common/locales/sv';
import localeSvExtra from '@angular/common/locales/extra/sv';
import { NoAccessPage, NotFoundPage, AboutPage } from './pages';
import { environment } from 'environments/environment';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
  MsalService
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  PublicClientApplication
} from '@azure/msal-browser';

registerLocaleData(localeSv, 'sv', localeSvExtra);

export function errorHandlerFactory() {
  if (environment.production) {
    return Sentry.createErrorHandler({ showDialog: true });
  }
  return new ErrorHandler();
}

@NgModule({
  declarations: [
    AppComponent,
    ContentComponent,
    AboutPage,
    NotFoundPage,
    NoAccessPage
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    MsalModule.forRoot(
      new PublicClientApplication({
        // MSAL Configuration
        auth: environment.auth,
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
          storeAuthStateInCookie: false // Deprecated, will be removed in future version
        },
        system: {
          loggerOptions: {
            piiLoggingEnabled: false
          }
        }
      }),
      {
        interactionType: InteractionType.Redirect // MSAL Guard Configuration
      },
      {
        interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
        protectedResourceMap: new Map([
          ['https://graph.microsoft.com/v1.0/me', ['user.read']],
          [
            `${environment.apiHost}*`,
            [environment.auth.apiScope]
          ]
        ])
      }
    ),
    CoreModule,
    AccountModule,
    NgxMdModule.forRoot(),
    // FragmentPolyfillModule.forRoot({ smooth: true }),
    SharedModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    // Keep this last so the wildcard route gets added last of all
    AppRoutingModule
  ],
  providers: [
    provideNgxWebstorage(
      withNgxWebstorageConfig({ prefix: 'ecom' }),
      withLocalStorage(),
      withSessionStorage()
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },

    ContentTitleService,
    { provide: LOCALE_ID, useValue: 'sv' },
    {
      provide: ErrorHandler,
      useFactory: errorHandlerFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ],
  bootstrap: [AppComponent, MsalRedirectComponent]
})
export class AppModule {
  constructor() {}
}
